import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Select from './Select'
import { FileText, Plus, X, Trash2, CheckCircle2, Search } from 'lucide-react'
import { getCarePlanTemplates, createCarePlanTemplate, deleteCarePlanTemplate } from '../services/carePlanTemplates'

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'prevention', label: 'Prevention' },
]

const CarePlanTemplateSelector = ({ onSelectTemplate, selectedTemplateId, currentPlanData }) => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'general',
  })
  const [saving, setSaving] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  useEffect(() => {
    if (organizationId) {
      fetchTemplates()
    }
  }, [organizationId])

  const fetchTemplates = async () => {
    if (!organizationId) return
    try {
      setLoading(true)
      const data = await getCarePlanTemplates(organizationId, true)
      setTemplates(data)
    } catch (error) {
      console.error('Error fetching templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTemplate = async () => {
    if (!templateForm.name.trim() || !organizationId || !currentPlanData) return

    try {
      setSaving(true)
      await createCarePlanTemplate(organizationId, {
        name: templateForm.name.trim(),
        description: templateForm.description.trim(),
        category: templateForm.category,
        templateData: {
          title: currentPlanData.title,
          summary: currentPlanData.summary,
          goals: currentPlanData.goals,
          status: currentPlanData.status,
        },
        isPublic: false,
        isDefault: false,
      })

      setTemplateForm({ name: '', description: '', category: 'general' })
      setShowCreateForm(false)
      fetchTemplates()
    } catch (error) {
      console.error('Error creating template:', error)
      alert(error.message || 'Failed to create template')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return
    try {
      await deleteCarePlanTemplate(templateId)
      fetchTemplates()
    } catch (error) {
      console.error('Error deleting template:', error)
      alert('Failed to delete template')
    }
  }

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = !searchTerm || template.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  if (loading) {
    return <div className="text-sm text-gray-500">Loading templates...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText size={20} />
          Care Plan Templates
        </h3>
        {canWrite('care_plans') && currentPlanData && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-1"
          >
            <Plus size={16} />
            Save as Template
          </Button>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          options={[{ value: 'all', label: 'All Categories' }, ...CATEGORIES]}
          className="w-40"
        />
      </div>

      {/* Create Template Form */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card variant="soft" className="p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">Create Template from Current Plan</h4>
                <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-3">
                <Input
                  label="Template Name"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g. Standard Wellness Plan"
                  required
                />
                <Input
                  label="Description"
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="When to use this template..."
                />
                <Select
                  label="Category"
                  options={CATEGORIES}
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                />
                <div className="flex gap-2">
                  <Button onClick={handleCreateTemplate} disabled={!templateForm.name.trim() || saving} className="flex-1">
                    {saving ? 'Saving...' : 'Save Template'}
                  </Button>
                  <Button variant="secondary" onClick={() => setShowCreateForm(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <Card variant="soft" className="p-6 text-center">
          <FileText size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No templates found</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filteredTemplates.map((template) => (
            <Card
              key={template.id}
              variant="soft"
              hover
              className={`p-4 cursor-pointer border-2 transition-all ${
                selectedTemplateId === template.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-transparent hover:border-primary-200'
              }`}
              onClick={() => onSelectTemplate && onSelectTemplate(template)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{template.name}</h4>
                    {template.is_default && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-700">
                        Default
                      </span>
                    )}
                    {template.is_public && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        Public
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 capitalize">
                      {template.category}
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
                  )}
                </div>
                {canWrite('care_plans') && template.organization_id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteTemplate(template.id)
                    }}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              {selectedTemplateId === template.id && (
                <div className="mt-2 pt-2 border-t border-primary-200">
                  <span className="text-xs text-primary-600 font-semibold flex items-center gap-1">
                    <CheckCircle2 size={12} />
                    Selected
                  </span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default CarePlanTemplateSelector

